 import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild
} from '@angular/core';

import { MatDialogRef } from '@angular/material/dialog';

@Component({
  selector: 'app-modal-global',
  templateUrl: './modal-global.component.html',
  styleUrls: ['./modal-global.component.css']
})
export class ModalGlobalComponent implements OnInit, OnChanges, OnDestroy {

  @Input() isOpen = false;
  @Input() titleText = '';
  @Input() descriptionText = '';
  @Input() badgeText = '';
  @Input() useBackdrop = true;
  @Input() hideHeader = false;
  @Input() size: 'small' | 'default' | 'wide' = 'default';
  @Input() usarMatDialog = false;

 
  @Input() disableClose = false;

  /**
   * Controla únicamente el cierre al hacer click fuera del modal (backdrop).
   * A diferencia de "disableClose" (que también bloquea la "X" y la tecla
   * Escape), esto deja esas otras formas de cerrar intactas: solo evita que
   * un click accidental fuera del modal lo cierre.
   */
  @Input() cerrarAlClicFuera = true;

  @Output() close = new EventEmitter<void>();

  @ViewChild('modalPanel', { static: false })
  modalPanel!: ElementRef<HTMLElement>;

  private elementoConFocoPrevio: HTMLElement | null = null;
  private overflowBodyPrevio: string | null = null;

  private readonly dentroDeMatDialog: boolean;

  private static readonly SELECTOR_ELEMENTOS_ENFOCABLES =
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';


  constructor(
    private renderer: Renderer2,
    private elementoHost: ElementRef<HTMLElement>,
    @Optional() private dialogRef: MatDialogRef<any>
  ) {
    this.dentroDeMatDialog = !!dialogRef;
  }

  ngOnInit(): void {


    /**
     * Solamente movemos el componente al body cuando NO está
     * siendo usado dentro de Angular Material.
     */

    if (!this.dentroDeMatDialog) {

      this.renderer.appendChild(
        document.body,
        this.elementoHost.nativeElement
      );

    } else {

      /**
       * Marcamos el .mat-dialog-container que envuelve a ESTE modal
       * con una clase propia, para que el CSS de app-modal-global solo
       * le quite el padding/fondo/overflow por defecto de Material a
       * ESE contenedor y no a los .mat-dialog-container de otros
       * MatDialog de la aplicación (ej. revisar-solicitud, ficha de
       * admisión, documentos del postulante) que no usan app-modal-global.
       */

      const contenedorMatDialog =
        this.elementoHost.nativeElement.closest('.mat-dialog-container');

      if (contenedorMatDialog) {
        this.renderer.addClass(
          contenedorMatDialog,
          'app-modal-global-container'
        );
      }

    }

  }

  ngOnChanges(changes: SimpleChanges): void {

    if (changes.isOpen) {
      

      if (changes.isOpen.currentValue) {
        this.alAbrirModal();
      } else {
        this.alCerrarModal();
      }

    }

  }

  ngOnDestroy(): void {
    

    this.alCerrarModal();

       /**
     * Solamente quitamos el nodo del body si nosotros
     * lo agregamos anteriormente.
     */

    if (!this.dentroDeMatDialog) {

      const nodo = this.elementoHost.nativeElement;

      if (nodo.parentNode) {
        this.renderer.removeChild(
          nodo.parentNode,
          nodo
        );
      }

    }

  }

  
  get claseTamano(): { [key: string]: boolean } {

    return {
      'app-modal--small': this.size === 'small',
      'app-modal--wide': this.size === 'wide'
    };

  }

  closeModal(): void {

    if (this.disableClose) {
      return;
    }

    this.close.emit();

  }

  onBackdropClick(): void {

    if (!this.cerrarAlClicFuera) {
      return;
    }

    this.closeModal();

  }

  @HostListener('document:keydown.escape')
  onEscapePress(): void {
   

    if (!this.isOpen) {
      return;
    }

    this.closeModal();

  }


  @HostListener('keydown.tab', ['$event'])
  onTabPress(event: KeyboardEvent): void {

    if (!this.isOpen || !this.modalPanel) {
      return;
    }


    const elementos = this.obtenerElementosEnfocables();

    if (!elementos.length) {
      return;
    }


    const primero = elementos[0];
    const ultimo = elementos[elementos.length - 1];

    if (event.shiftKey) {

      if (document.activeElement === primero) {

        event.preventDefault();

        ultimo.focus();

      }

    } else {

      if (document.activeElement === ultimo) {

        event.preventDefault();

        primero.focus();

      }

   
    }

  }

  private alAbrirModal(): void {

    this.elementoConFocoPrevio = document.activeElement as HTMLElement;
   
    /**
     * Angular Material ya bloquea el scroll.
     */

    if (!this.dentroDeMatDialog) {
      this.bloquearScrollBody();
    }

    setTimeout(() => {

      this.enfocarPrimerElemento();

    });

  }

  private alCerrarModal(): void {
       if (!this.dentroDeMatDialog) {
      this.restaurarScrollBody();
    }

    if (
      this.elementoConFocoPrevio &&
      typeof this.elementoConFocoPrevio.focus === 'function'
    ) {

      this.elementoConFocoPrevio.focus();

    }

    this.elementoConFocoPrevio = null;

  }

  private enfocarPrimerElemento(): void {

    if (!this.modalPanel) {
      return;
    }

    const foco =
           this.modalPanel.nativeElement.querySelector<HTMLElement>('[cdkFocusInitial]')
      || this.obtenerElementosEnfocables()[0]
      || this.modalPanel.nativeElement;

    foco.focus();

   
  }

  private obtenerElementosEnfocables(): HTMLElement[] {

    if (!this.modalPanel) {
      return [];
    }
   
    return Array
      .from(
        this.modalPanel.nativeElement.querySelectorAll<HTMLElement>(
          ModalGlobalComponent.SELECTOR_ELEMENTOS_ENFOCABLES
        )
      )
         .filter(el =>
        !!(
          el.offsetWidth ||
          el.offsetHeight ||
          el.getClientRects().length
        )
      );

  }

  private bloquearScrollBody(): void {

    if (this.overflowBodyPrevio !== null) {
    
      return;
    }

    this.overflowBodyPrevio = document.body.style.overflow || '';
    

    this.renderer.setStyle(
      document.body,
      'overflow',
      'hidden'
    );

  }

  private restaurarScrollBody(): void {

    if (this.overflowBodyPrevio === null) {
      return;
    }
      this.renderer.setStyle(
      document.body,
      'overflow',
      this.overflowBodyPrevio
    );

    this.overflowBodyPrevio = null;

  }

}